// TRANSCRIPTS ON A VISIT — what a screen draws for one prescription audio clip
// and for a recorded video visit (owner, 2 Oct 2026).
//
// Two AI features, two switches (each a tenant feature AND a doctor plan
// control — hooks/useVisitAiGates.js answers both):
//   ai_transcription            the prescription audio notes (visit clips)
//   ai_recording_transcription  the transcript of a recorded video visit
//
// A clip is uploaded and kept whether or not it is transcribed. Without
// `ai_transcription` the server stores it as `status: 'skipped'` with
// `transcription: 'off'`, answers the upload with `transcription: 'off'` and
// never makes a transcript. Nothing is then "generating": the screen says the
// audio is saved and never spins for it. A transcript made earlier is always
// shown. The upload answer's `transcription` is a STATUS word — 'queued' |
// 'failed' | 'off' (Backend/api/controllers/audioController.js) — never text.
//
// Pro-App twin: Mitra-OneCare-Pro-App/src/screens/visit/visitHelpers.js.

export const TRANSCRIPTION_OFF = 'off';

/** Did the server store this clip without a transcript? */
export const clipTranscriptionOff = (upload) =>
    upload?.transcription === TRANSCRIPTION_OFF || upload?.status === 'skipped';

/**
 * What to draw under one audio clip:
 *   'text'        its transcript — one made earlier is always shown
 *   'off'         none will come: "the audio is saved", never a spinner
 *   'failed'      the worker came back empty
 *   'processing'  still being transcribed
 *   'none'        nothing
 * `transcriptionOn` is the screen's own answer (useVisitAiGates).
 */
export const audioClipState = (upload, transcriptionOn = true) => {
    const text = upload?.transcribedNotes;
    if (typeof text === 'string' && text.trim()) return 'text';
    if (!transcriptionOn || clipTranscriptionOff(upload)) return 'off';
    if (upload?.status === 'failed') return 'failed';
    if (upload?.status === 'processing') return 'processing';
    return 'none';
};

/** Is this clip still waiting on its transcript — worth polling for? */
export const clipAwaitsTranscript = (upload, transcriptionOn = true) =>
    audioClipState(upload, transcriptionOn) === 'processing';

/**
 * The upload answer's `transcription: 'off'`, written onto the clip it names
 * (`uploadId`; the newest clip when the answer names none) — so a clip stored
 * without a transcript says so at once, whatever the screen's own gates
 * expected. Any other answer leaves the list as it is.
 */
export const stampUploadAnswer = (uploads, answer) => {
    if (!Array.isArray(uploads) || answer?.transcription !== TRANSCRIPTION_OFF) return uploads;
    const id = answer.uploadId;
    return uploads.map((u, i) => {
        const named = id ? u?.uploadId === id : i === uploads.length - 1;
        return named ? { ...u, transcription: TRANSCRIPTION_OFF } : u;
    });
};

/**
 * A recorded video visit's transcript state — the server's own
 * (`transcriptStatus`: done | pending | skipped | failed), except that where
 * `transcriptionOn` is false (ai_recording_transcription) anything but a
 * stored transcript is 'none': no "Generating…", no "unavailable", nothing.
 */
export const recordingTranscriptState = (rec, transcriptionOn = true) => {
    const hasText = typeof rec?.transcript === 'string' && !!rec.transcript.trim();
    const state = rec?.transcriptStatus || (hasText ? 'done' : 'pending');
    if (!transcriptionOn && !(state === 'done' && hasText)) return 'none';
    return state;
};
